import { WriteupState } from '@/store/writeup/state';
import { WriteupGetters } from '@/store/writeup/getters';
import { WriteupMutations } from '@/store/writeup/mutations';
import { Actions } from 'vuex-smart-module';
import Axios, { AxiosError } from 'axios';
import pick from 'lodash/pick';
import moment from 'moment';
import {
	protectionType,
	DealType,
	PaymentTermsPatchRequest,
	RebateSource, TradeInRequest,
	IncentiveRequest,
	UpdateOptionPayload,
	FiRequest,
	ContactRequest,
	AppointmentRequest,
	AppointmentResponseData,
	AccessoryRequest, AccessoryResponse, ShiftDigitalData, TealiumRequest, DataLayerOne, GtmData,
} from '@/types/writeup';

import {
	PlateToVinServiceRequest, TradeInServiceTypeAheadResponse, TradeInServiceTypeAheadRequest, TradeIn,
	TradeInServiceVehicle,
	TradeInFormData,
	TradeInCustomerVehicleInputParams, TradeInReportRequest,
} from '@/types/tradepending';
import { ServiceContainer, StorageService, WebsiteService, WriteupService, TradePendingService, AnalyticsService } from '@/services';
import { AppInitData } from '@/types/core';
import { captureException, setTag } from '@sentry/browser';
import { getCookie, SEARCH_TYPE, setCookie } from '@/common';
import { createSDScript } from '@/analytics/shift-degital-analytics';
import { createTealiumScript } from '@/analytics/tealium-analytics';
import { isMobile, generatePageUrl } from '@/common/helpers';
import { createGTMScript } from '@/analytics/gtm-analytics';
import md5 from 'md5';


interface WriteupActionsDependencies {
	services: ServiceContainer;
}

export class WriteupActions extends Actions<
	WriteupState,
	WriteupGetters,
	WriteupMutations,
	WriteupActions
	> {
	self = this;

	writeupService!: WriteupService

	storageService!: StorageService

	websiteService!: WebsiteService

	tradePendingService!: TradePendingService

	analyticsService!: AnalyticsService

	$setDependencies(dependencies: WriteupActionsDependencies): void {
		this.self.writeupService = dependencies.services.writeup;
		this.self.storageService = dependencies.services.storage;
		this.self.websiteService = dependencies.services.website;
		this.self.tradePendingService = dependencies.services.tradePending;
		this.self.analyticsService = dependencies.services.analytics;
	}

	async processError(payload?: AxiosError): Promise<void> {
		if (Axios.isCancel(payload)) {
			return Promise.resolve();
		}

		// reset all in progress states
		this.mutations.unsetWriteupUpdating();
		this.mutations.unsetTermsLoading();
		this.mutations.unsetDealTypeUpdating();
		this.mutations.unsetPendingDealType();
		this.mutations.unsetFetchingVehicleList();
		this.mutations.unsetFetchingVehicleDetails();
		this.mutations.unsetFiObjectUpdating();
		this.mutations.unsetIncentivesSaving();
		this.mutations.unsetTermsUpdating();

		this.mutations.setError();
		console.error(payload);
		setTag('info', 'writeupProcessError');
		captureException(payload);

		return Promise.resolve();
	}

	async changeDealType(type: DealType): Promise<void> {
		const id = this.getters.writeupObject?.writeupId;
		let response;

		if (id !== undefined) {
			this.mutations.setWriteupUpdating();
			this.mutations.setDealTypeUpdating();
			this.mutations.setPendingDealType(type);
			response = await this.writeupService.patch(id, { type })
				.catch(error => this.actions.processError(error));
		} else {
			return Promise.resolve();
		}

		if (response) {
			this.mutations.updateWriteup(response);
			this.mutations.unsetWriteupUpdating();

			if (type !== DealType.cash) {
				await this.actions.loadTerms();
			}
			const checkCustomerDetails = getCookie('dealerDiscountUser');

			if (response.pricing && response.pricing.priceAdjustments && (checkCustomerDetails !== '')) {
				await this.actions.addDealerDiscount(response.writeupId).then(() => {
					this.actions.loadRebates();
				});
			} else {
				await this.actions.loadRebates();
			}

			await this.actions.loadIncentives();
			this.mutations.unsetDealTypeUpdating();
			this.mutations.unsetPendingDealType();
		}

		return Promise.resolve();
	}

	async createWriteup({ vin, writeupId: initWriteupId, vdpUrl }: AppInitData): Promise<void> {
		const writeupId = initWriteupId || this.storageService.getWriteupId(vin);
		let response;

		if (this.getters.vehicleVin === vin) {
			return Promise.resolve();
		}

		this.mutations.cleanWriteup();

		if (writeupId !== null) {
			this.mutations.setWriteupUpdating();

			response = await this.writeupService.get(writeupId)
				.catch(error => this.actions.processError(error));
		}

		if (!response) {
			response = await this.writeupService.create(vin)
				.catch(error => this.actions.processError(error));
		}

		if (response) {
			this.actions.getGtmData(response.writeupId);
			await this.actions.getShiftDigitalData(response.writeupId);
			this.actions.getTealiumData({ writeupId: `${response.writeupId}`, vdpUrl, vin });
			this.storageService.saveWriteupId(vin, String(response.writeupId));
			this.mutations.updateWriteup(response);
			await this.actions.loadTerms();
			await this.actions.loadIncentives();
			this.mutations.updateFiObjects([]);
			const checkCustomerDetails = !!getCookie('dealerDiscountUser');

			if ((response.pricing.discounts === 0) && response.pricing.priceAdjustments && checkCustomerDetails) {
				await this.actions.addDealerDiscount(response.writeupId).then(() => {
					this.actions.loadRebates();
				});
			} else {
				await this.actions.loadRebates();
			}

			this.mutations.unsetWriteupUpdating();
		}

		this.mutations.unsetScheduledDriveStatus();

		return Promise.resolve();
	}

	async loadTerms(): Promise<void> {
		const id = this.getters.writeupObject?.writeupId;
		const creditScore = this.getters.writeupObject?.terms?.creditScore;
		const downPayment = this.getters.writeupObject?.terms?.down;
		const type = this.getters.writeupObject?.type;

		if (type === DealType.cash) return Promise.resolve();

		let response;

		if (id !== undefined) {
			this.mutations.setTermsLoading();
			response = await this.writeupService.getPaymentTerms(id, creditScore || undefined, downPayment || undefined)
				.catch(error => this.actions.processError(error));
		}

		if (response) {
			this.mutations.updateTerms(response);
			this.mutations.unsetTermsLoading();
		}

		return Promise.resolve();
	}

	async updateTerms(terms: Partial<PaymentTermsPatchRequest>): Promise<void> {
		const id = this.getters.writeupObject?.writeupId;
		const type = this.getters.writeupObject?.type;

		if (type === DealType.cash) return Promise.resolve();

		let response;

		if (id !== undefined) {
			this.mutations.setWriteupUpdating();
			Object.keys(terms).forEach(key => { this.mutations.setTermUpdating(key); });
			response = await this.writeupService.patchPaymentTerms(id, terms)
				.catch(error => this.actions.processError(error));
		}

		if (response) {
			this.mutations.updateWriteup(response);
			this.mutations.unsetWriteupUpdating();
			this.mutations.unsetTermsUpdating();
			await this.actions.loadTerms();
			await this.actions.loadRebates();
			await this.actions.loadIncentives();
		}

		return Promise.resolve();
	}

	async loadIncentives(): Promise<void> {
		const id = this.getters.writeupObject?.writeupId;
		const creditScore = this.getters.writeupObject?.terms?.creditScore;
		const downPayment = this.getters.writeupObject?.terms?.down;
		let response;

		if (id !== undefined) {
			response = await this.writeupService.getIncentives(id, creditScore, downPayment)
				.catch(error => this.actions.processError(error));
		}

		if (response) {
			const last = this.getters.lastIncentiveRequest;

			this.mutations.updateIncentives(response);
			if (last && !this.getters.incentives?.some(incentive => (
				incentive.id === last.id
				&& incentive.terms[0].months === last.term?.months
				&& incentive.terms[0].apr === last.term?.apr
				&& incentive.terms[0].miles === last.term?.miles
			))) {
				this.mutations.setLastIncentiveRequest(null);
			}
		}

		return Promise.resolve();
	}

	async saveSelectedIncentive(offer: IncentiveRequest): Promise<void> {
		const id = this.getters.writeupObject?.writeupId;
		let response;

		if (id !== undefined) {
			this.mutations.setWriteupUpdating();
			this.mutations.setIncentivesSaving();

			if (this.getters.writeupObject?.terms.isDefault) {
				const params = {
					apr: this.getters.writeupObject?.terms.apr,
					termLength: this.getters.writeupObject?.terms.termLength,
					programMiles: this.getters.writeupObject?.terms.programMiles,
					down: this.getters.writeupObject?.terms.down,
					userApr: this.getters.writeupObject?.terms.userApr,
					creditScore: this.getters.writeupObject?.terms.creditScore,
				};

				await this.actions.updateTerms(params);
			}
			response = await this.writeupService.saveSelectedOffer(id, offer)
				.catch(error => this.actions.processError(error));
		}

		if (response) {
			if (offer.id) {
				this.mutations.setLastIncentiveRequest(offer);
			} else if (this.getters.writeupObject?.selectedIncentives) {
				this.mutations.setLastIncentiveRequest({
					...pick(this.getters.writeupObject?.selectedIncentives[0], ['id', 'term']),
				});
			}

			await this.actions.loadRebates();
			this.mutations.updateWriteup(response);
			this.mutations.unsetWriteupUpdating();
			this.mutations.unsetIncentivesSaving();
		}

		return Promise.resolve();
	}

	async loadRebates(source?: RebateSource): Promise<void> {
		const payload = {
			id: this.getters.writeupObject?.writeupId,
			creditScore: this.getters.writeupObject?.terms?.creditScore,
			downPayment: this.getters.writeupObject?.terms?.down,
			source,
		};

		let response;

		if (payload.id !== undefined) {
			this.mutations.setRebatesUpdating();

			response = await this.writeupService.getRebates(payload)
				.catch(error => this.actions.processError(error));
		}

		if (response) {
			this.mutations.updateRebates(response);
			this.mutations.unsetRebatesUpdating();
		}

		return Promise.resolve();
	}

	async saveRebates(rebates: { id: number }[]): Promise<void> {
		const id = this.getters.writeupObject?.writeupId;

		let response;

		if (id !== undefined) {
			this.mutations.setWriteupUpdating();
			this.mutations.setRebatesUpdating();
			response = await this.writeupService.putRebates(id, rebates)
				.catch(error => this.actions.processError(error));
		}

		if (response) {
			this.mutations.updateWriteup(response);
			this.mutations.unsetWriteupUpdating();
			this.mutations.unsetRebatesUpdating();
		}

		return Promise.resolve();
	}

	async loadTradeIn(): Promise<void> {
		const id = this.getters.writeupObject?.writeupId;

		let response;

		if (id !== undefined) {
			this.mutations.setTradeInUpdating();
			response = await this.writeupService.getTradeIn(id)
				.catch(error => this.actions.processError(error));
		}
		if (response) {
			this.mutations.updateTradeIn(response);
			this.mutations.unsetTradeInUpdating();
		} else {
			await this.actions.processError();
		}

		return Promise.resolve();
	}

	async updateTradeIn(tradeIn: Partial<TradeInRequest>): Promise<void> {
		const id = this.getters.writeupObject?.writeupId;

		let response;

		this.mutations.setWriteupUpdating();

		if (id !== undefined) {
			response = await this.writeupService.saveTradeIn(id, tradeIn)
				.catch(error => this.actions.processError(error));
		}
		if (response) {
			this.mutations.updateWriteup(response);
			this.mutations.unsetWriteupUpdating();
		} else {
			await this.actions.processError();
		}
		return Promise.resolve();
	}

	handleTradeInCustomerData(params: PlateToVinServiceRequest) {
		return this.websiteService.getVin(params)
			.then(async response => {
				if (response.vin) {
					const { vin } = response;

					await this.actions.vinToVehicleDetails({ vin }).then(
						res => {
							const request = { formType: SEARCH_TYPE.plate };

							Object.assign(request, params);
							this.actions.saveTradeInEnterVehicleInputs(request);
						}
					);
					this.mutations.unsetVehicleSuggestionError();
				} else {
					this.mutations.setVehicleSuggestionError();
				}
				return Promise.resolve();
			})
			.catch(error => this.actions.processError(error));
	}

	vinToVehicleDetails(params: { vin: string }) {
		this.mutations.setFetchingVehicleDetails();
		return this.tradePendingService.vinToVehicleDetails(params)
			.then(response => {
				if (response.error) {
					this.mutations.setVehicleSuggestionError();
				} else {
					this.mutations.updateVehicleDetails(response);
					this.mutations.unsetFetchingVehicleDetails();
					this.mutations.unsetVehicleSuggestionError();
					const request = { formType: SEARCH_TYPE.vin };

					Object.assign(request, params);

					this.actions.saveTradeInEnterVehicleInputs(request);
				}
				return Promise.resolve();
			});
	}

	async loadFiObjects(): Promise<void> {
		const id = this.getters.writeupObject?.writeupId;
		let response;

		this.mutations.setFiObjectUpdating();
		if (id !== undefined) {
			response = await this.writeupService.getOptions(id)
				.catch(error => this.actions.processError(error));
		}
		if (response) {
			this.mutations.updateFiObjects(response);
			this.mutations.unsetFiObjectUpdating();
		}

		return Promise.resolve();
	}

	async loadOptions(): Promise<void> {
		const id = this.getters.writeupObject?.writeupId;
		let response;

		this.mutations.setFiObjectUpdating();
		if (id !== undefined) {
			response = await this.writeupService.getOptions(id)
				.catch(error => this.actions.processError(error));
		}
		if (response) {
			if (this.getters.optionProvider === protectionType.FAndI) {
				response.forEach(item => {
					const optionId = item.id;

					if (optionId) {
						this.actions.getFiTerms(optionId);
					}
					this.mutations.updateOptions(item);
				});
			}
			this.mutations.updateFiObjects(response);
			this.mutations.unsetFiObjectUpdating();
		}
		return Promise.resolve();
	}

	async getFiTerms(optionId: number) {
		const id = this.getters.writeupObject?.writeupId;

		await this.writeupService.getFiTerms(id, optionId)
			.then(fiTermsResponse => {
				if (fiTermsResponse !== null && fiTermsResponse.length) {
					this.mutations.setFiTerms({ optionId, fiTermsResponse });
					const { price } = fiTermsResponse[0];

					this.mutations.setPriceToFIObject({ optionId, price });
				}
			})
			.catch(error => this.actions.processError(error));
	}

	async getVehicleDetails(param: TradeInServiceTypeAheadResponse | string) {
		this.mutations.setFetchingVehicleDetails();
		const vehicleDetails = await this.tradePendingService.getVehicleDetails(param)
			.then(response => {
				if (response.error) {
					this.mutations.setVehicleSuggestionError();
				} else {
					this.mutations.updateVehicleDetails(response);
					this.mutations.unsetFetchingVehicleDetails();
					this.mutations.unsetVehicleSuggestionError();

					const request = { formType: SEARCH_TYPE.makeModel };

					Object.assign(request, param);
					this.actions.saveTradeInEnterVehicleInputs(request);
				}
			})
			.catch(error => this.actions.processError(error));

		return vehicleDetails;
	}

	async loadSuggestedVehicles(param: TradeInServiceTypeAheadRequest) {
		this.mutations.setFetchingVehicleList();
		const vehicleList = await this.tradePendingService.getSuggestedVehicles(param)
			.then(response => {
				this.mutations.updateVehicleList(response);
			})
			.catch(error => this.actions.processError(error));

		return vehicleList;
	}

	resetSuggestions() {
		this.mutations.resetSuggestions();
	}

	unsetVehicleSuggestionError() {
		this.mutations.unsetVehicleSuggestionError();
	}

	async updateOptions(selectedOptions: Array<FiRequest>) {
		const id = this.getters.writeupObject?.writeupId;

		let response;

		this.mutations.setWriteupUpdating();

		if (id !== undefined) {
			response = await this.writeupService.updateOptions(id, selectedOptions)
				.catch(error => this.actions.processError(error));
		}

		if (response) {
			this.mutations.updateWriteup(response);
			this.mutations.unsetWriteupUpdating();
		}
	}

	selectOption(payload: UpdateOptionPayload) {
		this.mutations.selectOption(payload);
	}

	addOption(payload: UpdateOptionPayload) {
		this.mutations.addOption(payload);
	}

	async saveTradeIn(tradeInOptions: TradeIn) {
		const id = this.getters.writeupObject?.writeupId;

		let response;

		this.mutations.setWriteupUpdating();

		if (id !== undefined) {
			response = await this.writeupService.saveTradeIn(id, tradeInOptions)
				.catch(error => this.actions.processError(error));
		}

		if (response) {
			this.mutations.updateWriteup(response);
			this.mutations.unsetWriteupUpdating();
		}
	}

	async changeZipCode(zipCode: string): Promise<void> {
		const id = this.getters.writeupObject?.writeupId;
		let response;

		if (id !== undefined) {
			this.mutations.setWriteupUpdating();
			response = await this.writeupService.patch(id, { zipCode });
		} else {
			return Promise.resolve();
		}

		if (response) {
			this.mutations.updateWriteup(response);
			await this.actions.loadTerms();
			await this.actions.loadRebates();
			await this.actions.loadIncentives();
			this.mutations.unsetWriteupUpdating();
		}

		return Promise.resolve();
	}

	async saveContact(payload: ContactRequest): Promise<void> {
		const id = this.getters.writeupObject?.writeupId;

		if (id !== undefined) {
			setCookie('customerInfo', JSON.stringify(payload), 90);
			this.writeupService.saveContactData(id, payload);
		}
		return Promise.resolve();
	}

	async sendAppointment(payload: AppointmentRequest): Promise<void> {
		const id = this.getters.writeupObject?.vehicle.dealerId;
		let response;

		if (id !== undefined) {
			try {
				this.mutations.setWriteupUpdating();
				response = await this.writeupService.sendAppointment(id, payload);
				if (response.isBusy) {
					this.mutations.setIsBusy(true);
				} else {
					this.mutations.setIsBusy(false);
				}
				this.mutations.unsetWriteupUpdating();
			} catch (e) {
				this.mutations.setIsBusy(false);
				this.mutations.unsetWriteupUpdating();
				throw e;
			}
		}

		if (response) {
			this.mutations.saveAppointmentRequestData(response);
		}

		return Promise.resolve();
	}

	async saveScheduleTestDrive(params: {
		writeupId: number|undefined;
		type: number;
		date: string;
		time: string;
		customerAddress: string;
		customerAddressNotes: string;
	}): Promise<void> {
		const id = this.getters.writeupObject?.writeupId;
		let response;

		if (id !== undefined) {
			this.mutations.setWriteupUpdating();
		}

		this.mutations.saveScheduleTestDriveData(params);
		this.mutations.unsetWriteupUpdating();

		return Promise.resolve();
	}

	async loadDateAppointment(type: number): Promise<void> {
		const id = this.getters.writeupObject?.writeupId;
		const dealerId = this.getters.writeupObject?.vehicle.dealerId;
		let response;

		if (id !== undefined && dealerId !== undefined) {
			this.mutations.setDatesAppointmentUpdating();
			response = await this.writeupService.getDatesAppointment(dealerId, type)
				.catch(error => this.actions.processError(error));
		}

		if (response) {
			this.mutations.updateDatesAppointment(response);
			this.mutations.unsetDatesAppointmentUpdating();
		}

		return Promise.resolve();
	}

	async loadTimeslotsAppointment(params: {
		type: number;
		date: string;
	}): Promise<void> {
		const id = this.getters.writeupObject?.writeupId;
		const dealerId = this.getters.writeupObject?.vehicle.dealerId;
		let response;

		if (id !== undefined && dealerId !== undefined) {
			this.mutations.setTimeslotsAppointmentUpdating();
			response = await this.writeupService.getTimeslotsAppointment(dealerId, params.type, params.date)
				.catch(error => this.actions.processError(error));
		}

		if (response) {
			this.mutations.updateTimeslotsAppointment(response);
			this.mutations.unsetTimeslotsAppointmentUpdating();
		}

		return Promise.resolve();
	}

	async loadTestDriveAppointmentData(): Promise<void> {
		const id = this.getters.writeupObject?.writeupId;
		const dealerId = this.getters.writeupObject?.vehicle.dealerId;
		let response;

		if (id !== undefined && dealerId !== undefined) {
			response = await this.writeupService.getTestDriveAppointmentData(dealerId, id)
				.catch(error => this.actions.processError(error));
		}

		if (response) {
			this.mutations.updateTestDriveAppointmentData(response);
		}

		return Promise.resolve();
	}

	async processSaveTradeIn(params: {
		formData: TradeInFormData;
		selectedVehicle: TradeInServiceVehicle;
	}) {
		this.mutations.setWriteupUpdating();
		const tradeIn: TradeIn = { tradingIn: true, userValue: false, ...params.formData };

		const { tradeInEnterVehicleInputs } = this.getters;

		//TODO - save search history & create a new pp api field for vin
		tradeIn.customerState = tradeInEnterVehicleInputs.state;
		tradeIn.plateNumber = tradeInEnterVehicleInputs.plate;
		tradeIn.vin = tradeInEnterVehicleInputs.vin;
		tradeIn.make = tradeInEnterVehicleInputs.make;
		tradeIn.model = tradeInEnterVehicleInputs.model;
		tradeIn.trim = tradeInEnterVehicleInputs.trim;
		tradeIn.year = tradeInEnterVehicleInputs.year;
		tradeIn.vehicle = params.selectedVehicle;

		const response = await this.websiteService.getTradeInValuation({
			mileage: tradeIn.mileage,
			payoff: tradeIn.payoff,
			vehicleId: String(params.selectedVehicle.id),
			zip: Number(this.getters.writeupObject?.zipCode),
		}).catch(error => this.actions.processError(error));

		if (response) {
			tradeIn.tradeinValue = response.tradeinValue;
			tradeIn.amount = response.amount;
			await this.actions.saveTradeIn(tradeIn);
		}
	}

	saveTradeInEnterVehicleInputs(params: TradeInCustomerVehicleInputParams) {
		const { tradeInEnterVehicleInputs } = this.getters;
		const fieldKeys = Object.keys(tradeInEnterVehicleInputs) as Array<keyof typeof tradeInEnterVehicleInputs>;
		const fieldKeysLength = fieldKeys.length;

		for (let i = 0; i < fieldKeysLength; i += 1) {
			const fieldKey = fieldKeys[i];

			tradeInEnterVehicleInputs[fieldKey] = '';
		}

		if (params.formType === SEARCH_TYPE.vin) {
			tradeInEnterVehicleInputs.vin = params.vin;
			tradeInEnterVehicleInputs.formType = params.formType;
		} else if (params.formType === SEARCH_TYPE.plate) {
			tradeInEnterVehicleInputs.plate = params.plate;
			tradeInEnterVehicleInputs.state = params.state;
			tradeInEnterVehicleInputs.formType = params.formType;
		} else if (params.formType === SEARCH_TYPE.makeModel) {
			tradeInEnterVehicleInputs.make = params.make;
			tradeInEnterVehicleInputs.model = params.model;
			tradeInEnterVehicleInputs.trim = params.trim;
			tradeInEnterVehicleInputs.year = params.year;
			tradeInEnterVehicleInputs.formType = params.formType;
		}
		this.mutations.setTradeInEnterVehicleInputs(tradeInEnterVehicleInputs);
	}

	getReport(payload: TradeInReportRequest) {
		this.mutations.setTradeInReportupdating();
		this.tradePendingService.getReport(payload).then(response => {
			this.mutations.updateTradeInReport(response);
			this.mutations.unsetTradeInReportupdating();
		}).catch(error => this.actions.processError(error));
	}

	async loadAccessories(): Promise<void> {
		const id = this.getters.writeupObject?.writeupId;
		let response;

		this.mutations.setAccessoryObjectUpdating();
		if (id !== undefined) {
			response = await this.writeupService.getAccessories(id)
				.catch(error => this.actions.processError(error));
		}
		if (response) {
			this.mutations.updateAccessoryObjects(response);
			this.mutations.unsetAccessoryObjectUpdating();
		}

		return Promise.resolve();
	}

	async updateAccessories(selectedAccessories: Array<AccessoryRequest>) {
		const id = this.getters.writeupObject?.writeupId;

		let response;

		this.mutations.setAccessoryObjectUpdating();

		if (id !== undefined) {
			response = await this.writeupService.updateAccessories(id, selectedAccessories)
				.catch(error => this.actions.processError(error));
		}

		if (response) {
			this.mutations.updateWriteup(response);
			this.mutations.unsetAccessoryObjectUpdating();
		}
	}

	async selectAccessory(id: number) {
		const payload = {
			id,
		};

		this.mutations.selectAccessory(payload);
		await this.updateAccessories(this.getters.selectedAccessories || []);
	}

	async getTaxesAndFees(): Promise<void> {
		const id = this.getters.writeupObject?.writeupId;
		let response;

		if (id !== undefined) {
			this.mutations.setWriteupUpdating();
			try {
				response = await this.writeupService.getTaxesAndFees(id);
			} catch (e) {
				this.mutations.unsetWriteupUpdating();
				throw e;
			}
		}

		if (response) {
			this.mutations.updateTaxesAndFees(response);
			this.mutations.unsetWriteupUpdating();
		}
	}

	async getShiftDigitalData(writeupId: number|undefined) {
		await this.writeupService.getShiftDigitalData(writeupId)
			.then(response => {
				const clientId = this.storageService.getClientId();
				const vin = this.getters.writeupObject?.vehicle.vin;
				const websiteId = this.getters.writeupObject?.websiteId;

				const dealId = md5(clientId + vin + websiteId);

				if (response) {
					createSDScript({ ...response, sessionId: clientId, dealId });
					this.mutations.setShiftDigitalData({ ...response, sessionId: clientId, dealId });
				}
			});
	}

	trackSdEvent(eventName: string) {
		const shiftDigitalDetails = this.getters.shiftDigitalData;

		if (shiftDigitalDetails) {
			this.analyticsService.sdEventTracking({ eventName, shiftDigitalData: shiftDigitalDetails });
		}
	}

	getGtmData(writeupId: number|undefined) {
		this.writeupService.getGtmData(writeupId)
			.then(response => {
				if (!Array.isArray(response)) {
					createGTMScript(response);
					this.mutations.setGtmData(response);
				}
			});
	}

	trackGtmEvent(eventData: any) {
		const gtmDetails = this.getters.gtmData;

		if (gtmDetails) {
			this.analyticsService.gtmEventTracking(eventData);
		}
	}

	/* eslint-disable @typescript-eslint/camelcase */
	getTealiumData(initData: AppInitData) {
		if (initData?.writeupId) {
			this.writeupService.getTealiumData(initData.writeupId)
				.then(response => {
					if (response && Object.keys(response).length > 0) {
						const scriptData = response;

						scriptData.dataLayer.page_url = generatePageUrl(initData);
						scriptData.dataLayer.layout_type = isMobile() ? 'mobile' : 'desktop';
						this.mutations.setTealiumData(scriptData);
						createTealiumScript(scriptData);
					}
				});
		}
	}

	/* eslint-disable @typescript-eslint/camelcase */
	async updateTealiumDataLayer(data: TealiumRequest) {
		const tealiumResponse = this.getters.tealiumData;

		const tealiumDataLayer = this.getters.tealiumData?.dataLayer;

		let events = {};
		let uTagEvent = 'view';

		if (tealiumResponse?.pageMapping && tealiumDataLayer) {
			await this.analyticsService.serverDateTime().then(res => {
				tealiumDataLayer.current_time = moment(res).utc().format('Y|M|D|HH|mm|ss');
			});
			tealiumDataLayer.page_name = `${tealiumDataLayer.site_section}:${tealiumDataLayer.site_sub_section}:${tealiumResponse.pageMapping[data.pageMapping as keyof typeof tealiumResponse.pageMapping]}`;
			tealiumResponse.dataLayer = tealiumDataLayer;

			this.mutations.setTealiumData(tealiumResponse);

			if (tealiumResponse?.linksMapping && data.linkMapping) {
				events = tealiumResponse?.linksMapping[data.linkMapping as keyof typeof tealiumResponse.linksMapping];
				uTagEvent = data.linkMapping === 'dealDetails' ? 'view' : 'link';
			} else if (tealiumResponse?.formMapping && data.formMapping) {
				uTagEvent = 'link';
				events = tealiumResponse?.formMapping[data.formMapping as keyof typeof tealiumResponse.formMapping];
			}
		}

		const eventData = {
			...tealiumDataLayer,
			...events,
			utag_event: uTagEvent,
			utag_version: (window as Window).utag !== undefined ? (window as Window).utag.cfg.v : '',
		};

		this.analyticsService.updateTealiumDataLayer(eventData);
	}

	setPpLoaded() {
		this.mutations.setPpLoaded();
	}

	unSetPpLoaded() {
		this.mutations.unSetPpLoaded();
	}

	async addDealerDiscount(writeupId: number|undefined): Promise<void> {
		const response = await this.writeupService.addDealerDiscount(writeupId)
			.catch(error => this.actions.processError(error));

		if (response) {
			this.mutations.updateWriteup(response);
		}

		return Promise.resolve();
	}

	setLoading() {
		return this.mutations.setLoading();
	}
}
